import { StrictMode, type ReactElement } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { RouterProvider, matchRoutes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import router, { routes } from './router';
import type { PreloadableComponent } from './lazyWithPreload';
import './index.css';

const container = document.getElementById('root')!;

const app = (
  <StrictMode>
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  </StrictMode>
);

/**
 * Resolve the code-split components for the current URL before hydrating, so
 * the first render pass does not suspend and discard the prerendered markup.
 */
async function preloadMatchedRoutes(): Promise<void> {
  const matches = matchRoutes(routes, window.location.pathname) ?? [];
  await Promise.all(
    matches.map((match) => {
      const element = match.route.element as ReactElement | undefined;
      const type = element?.type as Partial<PreloadableComponent> | undefined;
      return typeof type?.preload === 'function' ? type.preload() : undefined;
    }),
  );
}

// Prerendered routes (scripts/prerender.ts) ship real markup and must be
// hydrated — createRoot would discard it. The SPA fallback shell
// (app-shell.html) has an empty #root and must use createRoot instead.
if (container.firstElementChild) {
  preloadMatchedRoutes()
    .catch(() => undefined)
    .then(() => hydrateRoot(container, app));
} else {
  createRoot(container).render(app);
}
